import * as React from 'react';
import {
  IconButton,
  Menu,
  MenuItem,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CommonDataTable from '../../components/CommonDataTable';

/**
 * Row Action Menu - Exactly matching SalesOrderTable implementation
 */
function RowActionMenu({ params, onEditRow, onDeleteRow }) {
  // hooks always at top-level
  const [anchorEl, setAnchorEl] = React.useState(null);
  const open = Boolean(anchorEl);
  const handleClick = (e) => setAnchorEl(e.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const row = params?.row ?? {};

  // If none of the handlers provided, still return a placeholder (but we could return null)
  if (!onEditRow && !onDeleteRow) return null;

  return (
    <>
      <IconButton size="small" onClick={handleClick}>
        <MoreVertIcon />
      </IconButton>
      <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
        {onEditRow && (
          <MenuItem
            onClick={() => {
              onEditRow?.(row);
              handleClose();
            }}
          >
            <EditIcon fontSize="small" style={{ marginRight: 8 }} /> Edit
          </MenuItem>
        )}
        {onDeleteRow && (
          <MenuItem
            onClick={() => {
              const identifierForDelete = row.lineNumber ?? row.id;
              onDeleteRow?.(identifierForDelete);
              handleClose();
            }}
          >
            <DeleteIcon fontSize="small" style={{ marginRight: 8 }} /> Remove
          </MenuItem>
        )}
      </Menu>
    </>
  );
}

/**
 * Actions column definition - Exactly matching SalesOrderTable implementation
 */
const actionsCol = (onEditRow, onDeleteRow) => ({
  field: "actions",
  headerName: "",
  width: 120,
  sortable: false,
  filterable: false,
  align: "left", // Align the action button to the left
  renderCell: (params) => <RowActionMenu params={params} onEditRow={onEditRow} onDeleteRow={onDeleteRow} />,
});


// ✅ Data columns
const dataColumns = [
  { field: 'lineNo', headerName: 'Line#', width: 100 },
  { field: 'purchaseOrder', headerName: 'Purchase Order', width: 150 },
  { field: 'item', headerName: 'SKUID', width: 150 },
  { field: 'description', headerName: 'Description', width: 200 },
  { field: 'uom', headerName: 'UOM', width: 100 },
  { field: 'pack', headerName: 'Pack', width: 100 },
  { field: 'lpnNo', headerName: 'LPN No', width: 140 },
  { field: 'batchNum', headerName: 'Batch Num', width: 140 },
  { field: 'itemCube', headerName: 'Item Cube', type: 'number', width: 120 },
  { field: 'itemWeight', headerName: 'Item Weight', type: 'number', width: 120 },
  { field: 'status', headerName: 'Status', width: 120 },
  { field: 'qcRequired', headerName: 'QC Required', width: 120 },
  { field: 'orderedQty', headerName: 'Order Qty', width: 130, type: 'number' },
  { field: 'receivedQty', headerName: 'Received Qty', width: 130, type: 'number' },
  { field: 'openQty', headerName: 'Open Qty', width: 130, type: 'number' },
  // {field: 'receivedQty', headerName: 'Received Qty', width: 130, type: 'number' },
  { field: 'itemPOTolerance', headerName: 'Tolerance %', width: 130, type: 'number' },
  { field: 'unitPrice', headerName: 'Price per item', width: 130, type: 'number' },
  { field: 'discount', headerName: 'Discount %', width: 130, type: 'number' },
  { field: 'tax', headerName: 'Tax %', width: 130, type: 'number' },
  { field: 'totalPrice', headerName: 'Total Price', width: 130, type: 'number' },
  // { field: 'shortQty', headerName: 'Short Qty', width: 130, type: 'number' },
  // { field: 'excessQty', headerName: 'Excess Qty', width: 150 },
  //  { field: 'damagedQty', headerName: 'Damaged Qty', width: 130, type: 'number' },
  { field: 'qcAutoAdjust', headerName: 'QC Auto Adjust', width: 140 },

  // { field: 'editByUser', headerName: 'Edited By', width: 140 },
  // { field: 'editDate', headerName: 'Edited Date', width: 170 },
];

const initialVisible = [
  'actions', // actions column first
  'select',
  'lineNo',
  'purchaseOrder',
  'status',
  'qcRequired',
  'item',
  'description',
  'uom',
  'itemCube',
  'itemWeight',
  'receivedQty',
  'damagedQty',
  'excessQty', 
  'shortQty',
  'openQty',
  'itemVolume',
  'pack',
  'lpnNo',
  'batchNum',
  'orderedQty',
  'itemPOTolerance',
  'unitPrice',
  'discount',
  'tax',
  'totalPrice',
  'poLineUdf1',
  // 'createByUser',
  // 'createDate',
  'notes',
];

// ✅ Main Table Component
export default function DetailItemsTable({
  items = [],
  // onItemsChange,
  onEditItem,
  onDeleteItem,
  readOnly,
}) {
  const [rows, setRows] = React.useState([]);
  const [checkedIds, setCheckedIds] = React.useState([]);

  // Toggle checkbox
  const handleCheck = (id) => {
    setCheckedIds((prev) =>
      prev.includes(id) ? prev.filter((cid) => cid !== id) : [...prev, id]
    );
  };

  // ✅ Compute all columns with actions first - matching SalesOrderTable approach
  const allPossibleColumns = React.useMemo(() => {
    return [actionsCol(onEditItem, onDeleteItem), ...dataColumns];
  }, [onEditItem, onDeleteItem]);

  // ✅ Sync rows with items + attach actions
  React.useEffect(() => {
    console.log("DetailItemsTable: syncing rows with items", items);
    const rowsWithActions = items.map((item) => {
      const unitPrice = parseFloat(item.unitPrice) || 0;
      const orderedQty = parseFloat(item.orderedQty) || 0;
      const discount = parseFloat(item.discount) || 0;
      const tax = parseFloat(item.tax) || 0;

      // ✅ Calculate: (qty * price * (1 + tax/100)) * (1 - discount/100)
      const subtotal = orderedQty * unitPrice;
      const taxedSubtotal = subtotal * (1 + tax / 100);
      const totalPrice = taxedSubtotal * (1 - discount / 100);

      return {
        ...item,
        _checked: checkedIds.includes(item.id),
        totalPrice: parseFloat(totalPrice.toFixed(2)),
        onCheck: handleCheck,
      };
    });

    setRows(rowsWithActions);
  }, [items, checkedIds]);



  if (rows.length === 0) {
    return null;
  }

  return (
    <div>
      <CommonDataTable
        id="detail-items-table"
        allPossibleColumns={allPossibleColumns}
        initialVisible={initialVisible}
        initialRows={rows}
        height={400}
        pageSize={10}
        rowsPerPageOptions={[10, 20, 50]}
        forItems={true}
        forItemsexport={true}
        checkboxSelection={!readOnly}
      />
    </div>
  );
}

